export enum OpticalStudentEntryStatus {
  Matched = 'Matched',
  Unregistered = 'Unregistered',
  BookletMismatch = 'BookletMismatch',
  Ambiguous = 'Ambiguous',
  Duplicate = 'Duplicate',
  InvalidLine = 'InvalidLine',
  Skipped = 'Skipped',
}

export interface OpticalFormAnswerDetail {
  questionNumber: number
  examQuestionId: number
  studentOption?: string | null
  correctOption?: string | null
  isCorrect: boolean
  point?: number | null
  maxPoint?: number | null
  isCancelled: boolean
  isMultipleChoice: boolean
  acceptedOptions: string[]
}

export interface OpticalFormCandidateStudent {
  studentSemesterCourseId: number
  studentNumber: string
  name?: string | null
  surname?: string | null
}

export interface OpticalFormStudentEntry {
  entryId: string
  sourceFileName: string
  sourceLineNumber: number
  rawData: string
  classroomCode: string
  bookletCode: string
  studentNumber: string
  answers: string
  status: OpticalStudentEntryStatus
  studentSemesterCourseId?: number | null
  studentName?: string | null
  studentSurname?: string | null
  totalPoint?: number | null
  correctCount: number
  wrongCount: number
  blankCount: number
  isPrimaryMatch: boolean
  notes?: string | null
  candidates: OpticalFormCandidateStudent[]
  answerDetails: OpticalFormAnswerDetail[]
  previousStatus?: OpticalStudentEntryStatus | null
  previousIsPrimaryMatch?: boolean | null
  examTypeCode?: string
  originalBookletCode?: string
  originalStudentNumber?: string
  originalStudentName?: string | null
  originalStudentSurname?: string | null
}

export interface OpticalFormExamStudent {
  studentSemesterCourseId: number
  studentNumber: string
  name?: string | null
  surname?: string | null
}

export interface OpticalFormQuestion {
  questionNumber: number
  examQuestionId: number
  correctOption?: string | null
  point?: number | null
  effectivePoint?: number | null
  acceptedOptions: string[]
  isMultipleChoice: boolean
  isCancelled: boolean
  aiReviewComment?: string | null
  aiReviewSignature?: string | null
  aiReviewUpdatedAt?: string | null
  aiReviewExamId?: number | null
  aiReviewExamQuestionId?: number | null
  learningOutcomes?: OpticalFormQuestionOutcome[]
}

export type OpticalFormLearningOutcomeType = 'CourseOutcome' | 'OrganizationOutcome' | string

export interface OpticalFormQuestionOutcome {
  type: OpticalFormLearningOutcomeType
  outcomeId: number
  outcomeName?: string | null
  order?: number | null
}

export interface OpticalFormExamResult {
  examId: number
  examName: string
  courseName: string
  courseCode: string
  examType: string
  semesterName: string
  questionCount: number
  questions: OpticalFormQuestion[]
  students: OpticalFormExamStudent[]
  entries: OpticalFormStudentEntry[]
  missingStudents: OpticalFormExamStudent[]
  booklets: OpticalFormBooklet[]
  masterBookletCode?: string | null
  summary?: OpticalFormExamSummary | null
  objectionNote?: string | null
  effectiveQuestionPoints?: Record<number, number>
  questionScalingFactor?: number | null
}

export interface OpticalFormProcessResponse {
  exams: OpticalFormExamResult[]
  errors: string[]
}

export interface OpticalFormBookletQuestion {
  questionNumber: number
  examQuestionId: number
  point?: number | null
  effectivePoint?: number | null
  isMultipleChoice: boolean
  acceptedOptions: string[]
  opticalOrder?: number | null
}

export interface OpticalFormBooklet {
  code: string
  questions: OpticalFormBookletQuestion[]
}

export interface OpticalFormQuestionAnalysis {
  questionNumber: number
  examQuestionId: number
  respondentCount: number
  correctCount: number
  blankCount: number
  difficulty?: number | null
  discrimination?: number | null
  difficultyLabel?: string | null
  discriminationLabel?: string | null
  difficultyNeedsReview?: boolean | null
  discriminationNeedsReview?: boolean | null
  requiresAiReview?: boolean | null
  alphaIfDeleted?: number | null
  alphaDelta?: number | null
  itemRestCorrelation?: number | null
  aiReviewComment?: string | null
  aiReviewSignature?: string | null
  aiReviewUpdatedAt?: string | null
  aiReviewExamId?: number | null
  aiReviewExamQuestionId?: number | null
}

export interface OpticalFormExamSummary {
  totalEntries: number
  matchedEntries: number
  duplicateEntries: number
  unregisteredEntries: number
  ambiguousEntries: number
  bookletMismatchEntries: number
  skippedEntries: number
  invalidEntries: number
  averageScore?: number | null
  medianScore?: number | null
  standardDeviation?: number | null
  cronbachAlpha?: number | null
  cronbachAlpha01?: number | null
  kr20?: number | null
  questionAnalyses: OpticalFormQuestionAnalysis[]
  learningOutcomeReliabilities?: OpticalFormLearningOutcomeReliability[]
}

export interface OpticalFormLearningOutcomeReliability {
  type: OpticalFormLearningOutcomeType
  outcomeId: number
  outcomeName?: string | null
  questionCount: number
  cronbachAlpha?: number | null
  cronbachAlpha01?: number | null
  kr20?: number | null
  order?: number | null
}

export interface OpticalFormFinalizeEntryPayload {
  studentSemesterCourseId: number
  bookletCode: string
  answers: string
}

export interface OpticalFormFinalizeExamPayload {
  examId: number
  entries: OpticalFormFinalizeEntryPayload[]
  questionAdjustments: OpticalFormFinalizeQuestionAdjustment[]
  options?: OpticalFormFinalizeExamOptions
  objectionNote?: string | null
}

export interface OpticalFormFinalizePayload {
  examPeriodId: number
  exams: OpticalFormFinalizeExamPayload[]
}

export interface OpticalFormFinalizeQuestionAdjustment {
  examQuestionId: number
  acceptedOptions: string[]
  isCancelled: boolean
  bookletAcceptedOptions?: Record<string, string[]>
  acceptedQuestionItemIds?: number[]
}

export interface OpticalFormFinalizeExamOptions {
  applyWrongPenalty: boolean
  wrongCountForPenalty: number
  correctCountToSubtract: number
}

export interface OpticalFormFinalizeSummary {
  updatedStudentCount: number
  updatedResultCount: number
  studentSemesterCourseIds: number[]
}

export interface OpticalFormFinalizeResponse {
  message: string
  summary: OpticalFormFinalizeSummary
  errors: string[]
}

export interface OpticalQuestionReviewOption {
  label: string
  text: string
  isCorrect: boolean
  key?: string | null
  optionId?: number | null
}

export interface OpticalQuestionReviewRequestPayload {
  examId: number
  examQuestionId: number
  difficulty?: number | null
  discrimination?: number | null
  respondentCount: number
  correctCount: number
  blankCount: number
  difficultyLabel?: string | null
  discriminationLabel?: string | null
  questionText?: string | null
  options?: OpticalQuestionReviewOption[]
  correctOptionLabels?: string[]
}

export interface OpticalQuestionReviewResponse {
  comment: string
}

export interface ExamBookletMappingResponse {
  examId: number
  bookletCodes: string[]
  bookletQuestionOrder: Record<string, number[]>
  optionOrderByBooklet: Record<number, Record<string, number[]>>
  lastGeneratedAt?: string | null
}
